import React from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';

// Existing Subcomponents
import PortfolioSummary from './components/PortfolioSummary';
import PortfolioChart from './components/PortfolioChart';
import AssetAllocation from './components/AssetAllocation';

// --- INTERFACES ---
export interface Asset {
    symbole: string;
    quantite: number;
    prix_acquisition: number;
    cours_actuel: number;
    variation: number;
    valeur_initiale: number;
    valeur_actuelle: number;
    plus_minus_value: number;
    performance: number;
    sparkline_data: number[];
    nom_societe?: string;
}

export interface CalculatedMetrics {
    portfolioValue: number;
    totalValue: number;
    totalPL: number;
    totalPLPct: number;
    dayPL: number;
    dayPLPct: number;
    cashBalance: number;
    investedAmount: number;
}

interface DesktopMyAssetsProps {
    assets: Asset[];
    calculatedMetrics: CalculatedMetrics | null;
    portfolioHistory: { time: string; value: number }[];
}

const PerfPill: React.FC<{ value: number; children: React.ReactNode }> = ({ value, children }) => {
    const positive = value >= 0;
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                fontSize: '12px',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '999px',
                color: positive ? '#047857' : '#be123c',
                background: positive ? 'rgba(16, 185, 129, 0.12)' : 'rgba(244, 63, 94, 0.12)',
            }}
        >
            {children}
        </span>
    );
};

const AssetRow = ({ asset }: { asset: Asset }) => {
    const isPosDay = asset.variation >= 0;
    const sparklineData = asset.sparkline_data
        ? asset.sparkline_data.map((val, i) => ({ i, val }))
        : [];

    return (
        <tr className='myassets-row'>
            <td className='myassets-td'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className='myassets-avatar'>{asset.symbole.substring(0, 2)}</div>
                    <div>
                        <div style={{ fontWeight: 700, color: 'var(--text-heading)' }}>{asset.symbole}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                            {asset.nom_societe || asset.symbole}
                        </div>
                    </div>
                </div>
            </td>
            <td className='myassets-td myassets-td-right'>
                <div style={{ fontWeight: 600, color: 'var(--text-heading)' }}>
                    {asset.quantite.toLocaleString('fr-FR')}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    PRU : {asset.prix_acquisition.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
                </div>
            </td>
            <td className='myassets-td myassets-td-right'>
                <div style={{ fontWeight: 700, color: 'var(--text-heading)' }}>
                    {asset.cours_actuel.toLocaleString('fr-FR')}
                </div>
                <div style={{ marginTop: '4px' }}>
                    <PerfPill value={asset.variation}>
                        {isPosDay ? '+' : ''}
                        {asset.variation.toFixed(2)}%
                    </PerfPill>
                </div>
            </td>
            <td className='myassets-td' style={{ width: '128px' }}>
                <div style={{ height: '40px', width: '96px', marginLeft: 'auto' }}>
                    <ResponsiveContainer width='100%' height='100%'>
                        <LineChart data={sparklineData}>
                            <Line
                                type='monotone'
                                dataKey='val'
                                stroke={isPosDay ? '#10B981' : '#F43F5E'}
                                strokeWidth={2}
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </td>
            <td className='myassets-td myassets-td-right'>
                <div style={{ fontWeight: 700, color: 'var(--text-heading)' }}>
                    {asset.valeur_actuelle.toLocaleString('fr-FR')}
                </div>
                <div style={{ fontSize: '12px', fontWeight: 600, marginTop: '2px', color: asset.plus_minus_value >= 0 ? '#059669' : '#e11d48' }}>
                    {asset.plus_minus_value >= 0 ? '+' : ''}
                    {asset.plus_minus_value.toLocaleString('fr-FR')} ({asset.performance.toFixed(2)}%)
                </div>
            </td>
        </tr>
    );
};

const EmptyAssetsState: React.FC = () => (
    <div style={{ padding: '48px 20px', textAlign: 'center' }}>
        <div
            style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'var(--bg-hover)',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
            }}
        >
            <i className='material-icons' style={{ fontSize: '26px' }}>inbox</i>
        </div>
        <div style={{ color: 'var(--text-heading)', fontWeight: 700, marginBottom: '6px' }}>
            Vous n'avez pas encore d'actions
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '18px' }}>
            Passez votre premier ordre pour commencer à suivre vos positions ici.
        </div>
        <Link
            to='/transactions'
            style={{
                display: 'inline-block',
                padding: '10px 20px',
                background: '#2563EB',
                color: '#fff',
                textDecoration: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 700,
            }}
        >
            Commencer à trader
        </Link>
    </div>
);

const DesktopMyAssets: React.FC<DesktopMyAssetsProps> = ({ assets, calculatedMetrics, portfolioHistory }) => {
    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            <Disclaimer type="simulation" />

            {/* 1. Portfolio Summary Cards */}
            {calculatedMetrics && <PortfolioSummary metrics={calculatedMetrics} />}

            {/* 2. Charts Section */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '20px',
                    marginBottom: '20px',
                }}
            >
                <div style={{ flex: 2, minWidth: '60%' }}>
                    <PortfolioChart data={portfolioHistory} />
                </div>
                <div style={{ flex: 1 }}>
                    <AssetAllocation assets={assets} />
                </div>
            </div>

            {/* 3. Assets Table */}
            <Box>
                <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: 'var(--text-heading)' }}>
                            Détail des Actifs
                        </h3>
                        {assets.length > 0 && (
                            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>({assets.length})</span>
                        )}
                    </div>
                    {assets.length === 0 ? (
                        <EmptyAssetsState />
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: '14px', textAlign: 'left', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr className='myassets-thead-row'>
                                        <th className='myassets-th'>Actif</th>
                                        <th className='myassets-th myassets-td-right'>Position</th>
                                        <th className='myassets-th myassets-td-right'>Prix Actuel</th>
                                        <th className='myassets-th' style={{ textAlign: 'center' }}>7 Jours</th>
                                        <th className='myassets-th myassets-td-right'>Valorisation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assets.map((asset, index) => (
                                        <AssetRow key={index} asset={asset} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Box>
        </div>
    );
};

export default DesktopMyAssets;
